import {
  Badge,
  Box,
  Button,
  Container,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  Flex,
  HStack,
  Heading,
  Input,
  Spinner,
  Table,
  Tabs,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { FiPlus, FiTrash2 } from "react-icons/fi"
import { FaEdit } from "react-icons/fa"

import {
  type SchoolSetting,
  useSchoolSettings,
} from "@/hooks/api/useSchoolSettings"
import { usePermissions } from "@/hooks/usePermissions"
import { api } from "@/services/api"

export const Route = createFileRoute("/_layout/schoolSettings")({
  component: SchoolSettingsPage,
})

const CATEGORIES = [
  { key: "swim_level", label: "Swim Levels" },
  { key: "location", label: "Locations" },
  { key: "program_type", label: "Program Types" },
] as const

type Category = (typeof CATEGORIES)[number]["key"]

function SchoolSettingsPage() {
  const { hasPermission } = usePermissions()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<Category>("swim_level")
  const [showDialog, setShowDialog] = useState(false)
  const [editingSetting, setEditingSetting] = useState<SchoolSetting | null>(
    null,
  )
  const [formValue, setFormValue] = useState("")
  const [formLabel, setFormLabel] = useState("")
  const [formSortOrder, setFormSortOrder] = useState(0)

  const isAdmin = hasPermission("system:admin")

  const { items, isLoading } = useSchoolSettings()

  const filteredItems = items.filter((s) => s.category === activeTab)

  const createMutation = useMutation({
    mutationFn: async (data: {
      category: string
      value: string
      label: string
      sort_order: number
    }) => {
      return api.instance.post("/api/v1/school-settings/", data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["school-settings"] })
      closeDialog()
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: { value?: string; label?: string; sort_order?: number }
    }) => {
      return api.instance.put(`/api/v1/school-settings/${id}`, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["school-settings"] })
      closeDialog()
    },
  })

  const deactivateMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.instance.delete(`/api/v1/school-settings/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["school-settings"] })
    },
  })

  const openCreate = () => {
    setEditingSetting(null)
    setFormValue("")
    setFormLabel("")
    setFormSortOrder(filteredItems.length)
    setShowDialog(true)
  }

  const openEdit = (setting: SchoolSetting) => {
    setEditingSetting(setting)
    setFormValue(setting.value)
    setFormLabel(setting.label)
    setFormSortOrder(setting.sort_order)
    setShowDialog(true)
  }

  const closeDialog = () => {
    setShowDialog(false)
    setEditingSetting(null)
  }

  const handleSave = () => {
    if (editingSetting) {
      updateMutation.mutate({
        id: editingSetting.id,
        data: { value: formValue, label: formLabel, sort_order: formSortOrder },
      })
    } else {
      createMutation.mutate({
        category: activeTab,
        value: formValue,
        label: formLabel,
        sort_order: formSortOrder,
      })
    }
  }

  if (!isAdmin) {
    return (
      <Container maxW="container.xl" py={8}>
        <Text>You don't have permission to manage school settings.</Text>
      </Container>
    )
  }

  return (
    <Container maxW="container.xl" py={{ base: 4, md: 8 }}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg">School Settings</Heading>
          <Text color="fg.muted" mt={1}>
            Manage swim levels, locations, and program types
          </Text>
        </Box>
      </Flex>

      <Tabs.Root
        value={activeTab}
        onValueChange={(e) => setActiveTab(e.value as Category)}
      >
        <Tabs.List mb={4}>
          {CATEGORIES.map((cat) => (
            <Tabs.Trigger key={cat.key} value={cat.key}>
              {cat.label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        {CATEGORIES.map((cat) => (
          <Tabs.Content key={cat.key} value={cat.key}>
            <Flex justify="flex-end" mb={4}>
              <Button size="sm" colorPalette="blue" onClick={openCreate}>
                <HStack gap={1}>
                  <FiPlus />
                  <Text>Add {cat.label.slice(0, -1)}</Text>
                </HStack>
              </Button>
            </Flex>

            {isLoading ? (
              <Flex justify="center" p={8}>
                <Spinner size="lg" />
              </Flex>
            ) : filteredItems.length === 0 ? (
              <Box
                borderWidth="1px"
                borderRadius="lg"
                p={8}
                textAlign="center"
              >
                <Text color="fg.muted">
                  No {cat.label.toLowerCase()} configured yet.
                </Text>
              </Box>
            ) : (
              <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
                <Table.Root variant="outline" size="sm">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader w="60px">Order</Table.ColumnHeader>
                      <Table.ColumnHeader>Value</Table.ColumnHeader>
                      <Table.ColumnHeader>Label</Table.ColumnHeader>
                      <Table.ColumnHeader w="100px">Status</Table.ColumnHeader>
                      <Table.ColumnHeader w="120px">Actions</Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {filteredItems
                      .sort((a, b) => a.sort_order - b.sort_order)
                      .map((setting) => (
                        <Table.Row key={setting.id}>
                          <Table.Cell>{setting.sort_order}</Table.Cell>
                          <Table.Cell>
                            <Text fontWeight="medium">{setting.value}</Text>
                          </Table.Cell>
                          <Table.Cell>{setting.label}</Table.Cell>
                          <Table.Cell>
                            <Badge
                              colorPalette={
                                setting.is_active ? "green" : "gray"
                              }
                            >
                              {setting.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </Table.Cell>
                          <Table.Cell>
                            <HStack gap={1}>
                              <Button
                                size="xs"
                                variant="ghost"
                                onClick={() => openEdit(setting)}
                              >
                                <FaEdit />
                              </Button>
                              {setting.is_active && (
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  colorPalette="red"
                                  onClick={() =>
                                    deactivateMutation.mutate(setting.id)
                                  }
                                >
                                  <FiTrash2 />
                                </Button>
                              )}
                            </HStack>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                  </Table.Body>
                </Table.Root>
              </Box>
            )}
          </Tabs.Content>
        ))}
      </Tabs.Root>

      {/* Create / Edit Dialog */}
      <DialogRoot
        open={showDialog}
        onOpenChange={(e) => !e.open && closeDialog()}
        size="sm"
      >
        <DialogBackdrop />
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingSetting ? "Edit Setting" : "Add Setting"}
            </DialogTitle>
            <DialogCloseTrigger />
          </DialogHeader>
          <DialogBody>
            <VStack gap={4}>
              <Box w="full">
                <Text fontSize="sm" fontWeight="medium" mb={1}>
                  Value
                </Text>
                <Input
                  value={formValue}
                  onChange={(e) => setFormValue(e.target.value)}
                  placeholder="e.g. Mountain View"
                />
              </Box>
              <Box w="full">
                <Text fontSize="sm" fontWeight="medium" mb={1}>
                  Label
                </Text>
                <Input
                  value={formLabel}
                  onChange={(e) => setFormLabel(e.target.value)}
                  placeholder="Display label"
                />
              </Box>
              <Box w="full">
                <Text fontSize="sm" fontWeight="medium" mb={1}>
                  Sort Order
                </Text>
                <Input
                  type="number"
                  value={formSortOrder}
                  onChange={(e) => setFormSortOrder(Number(e.target.value))}
                />
              </Box>
            </VStack>
          </DialogBody>
          <DialogFooter>
            <HStack gap={3}>
              <Button variant="ghost" onClick={closeDialog}>
                Cancel
              </Button>
              <Button
                colorPalette="blue"
                onClick={handleSave}
                loading={createMutation.isPending || updateMutation.isPending}
                disabled={!formValue.trim() || !formLabel.trim()}
              >
                {editingSetting ? "Save Changes" : "Add"}
              </Button>
            </HStack>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </Container>
  )
}
